import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { expensesApi } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Table, TableHeader, TableHead, TableBody, TableRow, TableCell,
} from './ui/table';
import {
  Plus, Pencil, Trash2, Receipt, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_LABELS = {
  office: 'Office',
  parking: 'Parking',
  rent: 'Rent',
  utilities: 'Utilities',
  insurance: 'Insurance',
  software: 'Software',
  marketing: 'Marketing',
  professional_fees: 'Professional Fees',
  supplies: 'Supplies',
  travel: 'Travel',
  meals: 'Meals',
  other: 'Other',
};

const CATEGORY_COLORS = {
  office: 'bg-blue-100 text-blue-700',
  parking: 'bg-purple-100 text-purple-700',
  rent: 'bg-rose-100 text-rose-700',
  utilities: 'bg-amber-100 text-amber-700',
  insurance: 'bg-cyan-100 text-cyan-700',
  software: 'bg-indigo-100 text-indigo-700',
  marketing: 'bg-pink-100 text-pink-700',
  professional_fees: 'bg-emerald-100 text-emerald-700',
  supplies: 'bg-slate-100 text-slate-700',
  travel: 'bg-teal-100 text-teal-700',
  meals: 'bg-orange-100 text-orange-700',
  other: 'bg-gray-100 text-gray-700',
};

const blank = { date: new Date().toISOString().split('T')[0], name: '', category: 'office', amount: '', notes: '', vendor: '' };

export default function ExpensesTab({ startDate, endDate, onTotalChange }) {
  const [expenses, setExpenses] = useState([]);
  const [categories, setCategories] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [saving, setSaving] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (startDate) filters.start_date = startDate;
      if (endDate) filters.end_date = endDate;
      const [r, sr] = await Promise.all([
        expensesApi.getAll(filters),
        startDate && endDate ? expensesApi.getSummary(startDate, endDate) : Promise.resolve({ data: null }),
      ]);
      setExpenses(r.data || []);
      setSummary(sr.data);
      if (sr.data && onTotalChange) onTotalChange(sr.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, onTotalChange]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    expensesApi.getCategories().then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(blank);
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({ ...blank, ...e, amount: String(e.amount) });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.name.trim() || !form.amount) {
      toast.error('Name and amount are required');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, amount: parseFloat(form.amount) };
      if (editing) {
        await expensesApi.update(editing.id, payload);
        toast.success('Expense updated');
      } else {
        await expensesApi.create(payload);
        toast.success('Expense added');
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete "${e.name}"?`)) return;
    try {
      await expensesApi.delete(e.id);
      toast.success('Deleted');
      await load();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // ---- Bulk delete (mirrors Loads/Deductions UX)
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      const ids = expenses.map(x => x.id);
      const all = ids.every(id => prev.has(id));
      return new Set(all ? [] : ids);
    });
  };
  const bulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete ${ids.length} expense${ids.length !== 1 ? 's' : ''}?`)) return;
    try {
      await expensesApi.bulkDelete(ids);
      toast.success(`Deleted ${ids.length}`);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error('Bulk delete failed');
    }
  };

  const grandTotal = useMemo(() =>
    expenses.reduce((s, e) => s + (e.amount || 0), 0), [expenses]);

  return (
    <div className="space-y-4">
      {/* Action bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted-foreground">
            Track company overhead like office, parking, utilities. These reduce your net profit on the Overview tab.
          </p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-expense-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Expense
        </Button>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card>
          <CardContent className="p-4">
            <p className="text-[10px] uppercase text-muted-foreground">Total expenses</p>
            <p className="text-2xl font-bold text-rose-600">${grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
            <p className="text-[11px] text-muted-foreground mt-1">{expenses.length} entries</p>
          </CardContent>
        </Card>
        {summary && summary.by_category.slice(0, 3).map(c => (
          <Card key={c.category}>
            <CardContent className="p-4">
              <p className="text-[10px] uppercase text-muted-foreground truncate">{CATEGORY_LABELS[c.category] || c.category}</p>
              <p className="text-2xl font-bold">${c.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
              <p className="text-[11px] text-muted-foreground mt-1">
                {grandTotal > 0 ? `${Math.round((c.amount / grandTotal) * 100)}% of total` : '—'}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <Card className="bg-rose-50 border-rose-200 dark:bg-rose-950/20 dark:border-rose-900">
          <CardContent className="p-3 flex items-center justify-between gap-3">
            <div className="text-sm font-medium text-rose-700">{selectedIds.size} selected</div>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedIds(new Set())}>Clear</Button>
              <Button variant="destructive" size="sm" onClick={bulkDelete}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete {selectedIds.size}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[44px]">
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer"
                    checked={expenses.length > 0 && expenses.every(e => selectedIds.has(e.id))}
                    onChange={toggleSelectAll}
                    data-testid="expense-select-all"
                  />
                </TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Vendor</TableHead>
                <TableHead className="text-right">Amount</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>
              ) : expenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Receipt className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-sm font-semibold">No expenses yet</p>
                    <p className="text-xs text-muted-foreground">Click "Add Expense" to start tracking company overhead.</p>
                  </TableCell>
                </TableRow>
              ) : (
                expenses.map(e => (
                  <TableRow key={e.id} data-testid={`expense-row-${e.id}`}>
                    <TableCell className="w-[44px]">
                      <input
                        type="checkbox"
                        className="h-4 w-4 cursor-pointer"
                        checked={selectedIds.has(e.id)}
                        onChange={() => toggleSelect(e.id)}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{e.date}</TableCell>
                    <TableCell className="font-medium">
                      {e.name}
                      {e.notes && <p className="text-xs text-muted-foreground mt-0.5 truncate max-w-[260px]">{e.notes}</p>}
                    </TableCell>
                    <TableCell>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${CATEGORY_COLORS[e.category] || CATEGORY_COLORS.other}`}>
                        {CATEGORY_LABELS[e.category] || e.category}
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.vendor || '—'}</TableCell>
                    <TableCell className="text-right font-bold text-rose-600">
                      ${e.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(e)}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => remove(e)}><Trash2 className="h-4 w-4 text-rose-500" /></Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle className="font-manrope">{editing ? 'Edit expense' : 'Add expense'}</DialogTitle>
            <DialogDescription>
              Company overhead — these are deducted from net profit on the Accounting Overview tab.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 pt-2">
            <div>
              <Label>Date *</Label>
              <Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} />
            </div>
            <div>
              <Label>Amount *</Label>
              <Input type="number" step="0.01" placeholder="0.00" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Name *</Label>
              <Input placeholder="e.g. Office rent — May" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(categories.length ? categories : Object.keys(CATEGORY_LABELS)).map(c => (
                    <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor (optional)</Label>
              <Input placeholder="e.g. WeWork" value={form.vendor} onChange={e => setForm({ ...form, vendor: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Notes</Label>
              <Input placeholder="Receipt #, period covered, anything…" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="expense-submit">
              {saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              {editing ? 'Save' : 'Add expense'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
